import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Icon from "components/ShellV2/Icon";
import {
  closeNotificationPopup,
  closeSuccessAlert,
  selectNotificationPopup,
  selectSuccessAlert,
} from "state/application/applicationSlice";
import { extractErrorMessage } from "utils/errors";

const KIND = {
  success: { icon: "check_circle", cls: "ok" },
  error: { icon: "error", cls: "err" },
  warning: { icon: "warning", cls: "warn" },
  info: { icon: "info", cls: "info" },
  delete: { icon: "delete", cls: "del" },
};

const DURATION = 3500;

/* Bottom-center toast host (port of the design's window.__toast). Subscribes to
   the redux notification state so every existing openSuccessAlert /
   openNotificationPopup dispatch renders as a toast — replacing the SweetAlert
   success modal and the MUI error snackbar. */
export default function Toasts() {
  const { t: translate } = useTranslation(["common"]);
  const dispatch = useDispatch();
  const successAlert = useSelector(selectSuccessAlert);
  const notificationPopup = useSelector(selectNotificationPopup);

  const [toasts, setToasts] = useState([]);
  const seq = useRef(0);

  const remove = useCallback((id) => {
    // animate out, then unmount
    setToasts((list) => list.map((t) => (t.id === id ? { ...t, leaving: true } : t)));
    setTimeout(() => setToasts((list) => list.filter((t) => t.id !== id)), 280);
  }, []);

  const push = useCallback(
    (kind, message) => {
      if (!message) return;
      seq.current += 1;
      const id = seq.current;
      setToasts((list) => [...list, { id, kind, message, leaving: false }]);
      setTimeout(() => remove(id), DURATION);
    },
    [remove]
  );

  // Success alert (was the SweetAlert "Adicionado" modal)
  useEffect(() => {
    if (!successAlert?.active) return;
    push("success", successAlert.description || successAlert.title);
    dispatch(closeSuccessAlert());
  }, [successAlert?.active, successAlert, push, dispatch]);

  // Notification popup (was the "Erro!" snackbar) — error or success
  useEffect(() => {
    if (!notificationPopup?.active) return;
    const kind = notificationPopup.type === "error" ? "error" : "success";
    push(kind, extractErrorMessage(notificationPopup.notification));
    dispatch(closeNotificationPopup());
  }, [notificationPopup?.active, notificationPopup, push, dispatch]);

  if (toasts.length === 0) return null;

  return (
    <div className="rec-toast-host">
      {toasts.map((t) => {
        const m = KIND[t.kind] || KIND.success;
        return (
          <div key={t.id} className={`rec-toast ${m.cls}${t.leaving ? " leaving" : ""}`}>
            <span className="rec-toast-ic">
              <Icon name={m.icon} fill />
            </span>
            <span className="rec-toast-msg">{t.message}</span>
            <button
              type="button"
              className="rec-toast-x"
              aria-label={translate("close")}
              onClick={() => remove(t.id)}
            >
              <Icon name="close" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
